import { useCallback, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../utils/config';
import { useAuth } from '../../Context/AuthContext';
import { createManualRecorder } from './manualRunRecorder';
import { ensureArcadeIdentity } from './ensureArcadeIdentity';
import { uploadErrorInfo } from './arcadeTheme';
import { SAVE_NEEDS_ACCOUNT_MESSAGE } from './ManualSaveNotice';

/**
 * Record client-side manual play and POST /api/runs when the episode ends.
 * If the player has no JWT (anonymous arcade visit), creates a guest session
 * via /guest-login so the run is still persisted under a guest user_id.
 */
export function useManualRunRecorder({ gameId, session, copyToMoveHistory = false } = {}) {
    const { token, user, enterGuest } = useAuth() || {};
    const recRef = useRef(null);
    if (!recRef.current) recRef.current = createManualRecorder();
    // `saveNotice` is null, or { message, needsAccount } after a failed save.
    // It is mirrored into session.setManualSaveNotice (useArcadeAgentSession)
    // so ArcadeGameShell can show it without per-page wiring. The session is
    // read through a ref so reset() keeps a stable identity.
    const [saveNotice, setSaveNotice] = useState(null);
    const sessionRef = useRef(session);
    sessionRef.current = session;
    const report = useCallback((notice) => {
        setSaveNotice(notice);
        const setter = sessionRef.current?.setManualSaveNotice;
        if (typeof setter === 'function') setter(notice);
    }, []);

    const reset = useCallback((meta) => {
        recRef.current.reset(meta);
        report(null);
        // Warm guest identity at episode start so finalize does not race auth.
        if (!token && typeof enterGuest === 'function') {
            ensureArcadeIdentity({ token, user, enterGuest }).catch(() => {});
        }
    }, [token, user, enterGuest, report]);

    const recordTick = useCallback((tick) => {
        recRef.current.recordTick(tick);
    }, []);

    const finalizeAndUpload = useCallback(async ({ score, extra } = {}) => {
        const rec = recRef.current;
        if (rec.finalized || rec.inFlight) return null;
        if (!gameId) return null;

        const identity = await ensureArcadeIdentity({ token, user, enterGuest });
        if (!identity?.token) {
            console.warn('Manual run submit skipped: could not obtain guest/user JWT', gameId);
            report({ message: SAVE_NEEDS_ACCOUNT_MESSAGE, needsAccount: true });
            return null;
        }

        rec.finalized = true;
        rec.inFlight = true;
        const displayUser = identity.user || user;
        const payload = rec.buildPayload({
            gameId,
            score,
            extra,
            copyToMoveHistory,
            username: displayUser?.username || displayUser?.name || displayUser?.display_name,
        });
        try {
            const res = await fetch(API_ENDPOINTS.SUBMIT_RUN, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${identity.token}`,
                },
                body: JSON.stringify(payload),
            });
            let json = {};
            try {
                json = await res.json();
            } catch {
                json = {};
            }
            if (res.ok && typeof session?.refreshLeaderboard === 'function') {
                await session.refreshLeaderboard();
            }
            if (!res.ok) {
                // 401 = not logged in, 403 = guest, 400 = implausible / no-move run.
                // Prefer the server's message (never a generic "session expired").
                const info = uploadErrorInfo(res.status, json, 'Score not saved.');
                const message = info.needsAccount ? SAVE_NEEDS_ACCOUNT_MESSAGE : info.message;
                console.info('Manual run not saved', gameId, res.status, info.message);
                report({ message, needsAccount: info.needsAccount });
                return {
                    ok: false,
                    status: res.status,
                    message,
                    serverMessage: info.message,
                    needsAccount: info.needsAccount,
                    payload,
                    response: json,
                };
            }
            report(null);
            return { ok: true, status: res.status, payload, response: json };
        } catch (err) {
            console.warn('Manual run submit failed', gameId, err);
            report({ message: 'Score not saved. Check your connection and try again.', needsAccount: false });
            return { ok: false, payload, error: String(err) };
        } finally {
            rec.inFlight = false;
        }
    }, [token, gameId, session, copyToMoveHistory, user, enterGuest, report]);

    const api = useMemo(
        () => ({ reset, recordTick, finalizeAndUpload, saveNotice: null }),
        [reset, recordTick, finalizeAndUpload],
    );
    // Attach the notice without changing `api` identity: pages list `recorder`
    // in effect deps, and a save result must not re-run those effects.
    api.saveNotice = saveNotice;
    return api;
}
